import React from "react";
import { MessageSquare, ExternalLink, X, Info } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { useModalDismiss } from "@/hooks/useModalDismiss";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  entityName: string;
  entityId: string;
  entityType: 'LE' | 'BR';
  legalEntityId: string;
}

export const WhatsappOnboardingModal: React.FC<Props> = ({ isOpen, onClose, entityName, entityId, entityType, legalEntityId }) => {
  const { onOverlayClick } = useModalDismiss(isOpen, onClose);

  if (!isOpen) return null;

  const handleConnect = () => {
    const META_APP_ID = import.meta.env.VITE_META_APP_ID;
    const CONFIG_ID = import.meta.env.VITE_META_CONFIG_ID;
    const REDIRECT_URI = encodeURIComponent(window.location.origin + "/messages/verifycode");

    const stateObj = { entityId, entityType, legalEntityId };
    const stateParam = encodeURIComponent(btoa(JSON.stringify(stateObj)));

    // Meta Embedded Signup URL
    const metaUrl = `https://www.facebook.com/v26.0/dialog/oauth?` +
      `client_id=${META_APP_ID}&` +
      `redirect_uri=${REDIRECT_URI}&` +
      `response_type=code&` +
      `config_id=${CONFIG_ID}&` +
      `state=${stateParam}`;
      
    window.open(metaUrl, "_blank");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-bridge-fg/20 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onOverlayClick}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="px-8 py-6 border-b border-bridge-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-xl text-green-600">
              <MessageSquare className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-bridge-fg">Connect WhatsApp</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-bridge-bg rounded-full transition-colors">
            <X className="w-5 h-5 text-bridge-muted" />
          </button>
        </div>

        <div className="p-8 space-y-6">
          <div className="space-y-2">
            <p className="text-bridge-fg font-medium">You are about to connect WhatsApp Business for:</p>
            <div className="p-4 bg-slate-50 rounded-2xl border border-bridge-border">
              <span className="text-lg font-bold text-bridge-fg">{entityName}</span>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3 text-blue-800 text-sm">
            <Info className="w-5 h-5 shrink-0 text-blue-400" />
            <p>
              Clicking the button below will redirect you to Meta's secure onboarding page. You will need a Facebook Business Account and access to the phone number you provided.
            </p>
          </div>

          <div className="flex flex-col gap-3 pt-2">
            <Button 
              onClick={handleConnect}
              className="bg-green-600 hover:bg-green-700 text-white rounded-2xl h-14 text-lg font-bold shadow-lg shadow-green-200"
            >
              Connect Now
              <ExternalLink className="w-5 h-5 ml-2" />
            </Button>
            
            {import.meta.env.MODE === 'development' && (
              <Button 
                variant="outline"
                onClick={async () => {
                  try {
                    const branchId = entityType === 'BR' ? entityId : undefined;
                    const dummyCode = "dummy_emulator_code_" + Date.now();
                    const { communicationChannelsService } = await import('@/services/communicationChannelsService');
                    await communicationChannelsService.onboardWhatsapp(dummyCode, legalEntityId, branchId, "EMULATOR");
                    onClose();
                    window.location.reload(); // Quick refresh to show state change
                  } catch (e) {
                    console.error("Emulator connect failed", e);
                  }
                }}
                className="rounded-2xl h-12 border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-300 transition-colors"
              >
                [Dev] Connect via Emulator
              </Button>
            )}

            <Button 
              variant="secondary" 
              onClick={onClose}
              className="rounded-2xl h-12"
            >
              Maybe Later
            </Button>
          </div>
        </div>
        
        <div className="px-8 py-4 bg-slate-50 border-t border-bridge-border text-center">
          <p className="text-[10px] text-bridge-muted uppercase font-bold tracking-widest">Powered by Meta Business SDK</p>
        </div>
      </div>
    </div>
  );
};
